import type { ServiceRequestStatus } from '../types/serviceRequestListType';

/**
 * Statuses that are subject to the 24-hour lead-time rule for cancellation.
 *
 * Per spec (RR-611), an admin cannot cancel a service request whose status is
 * `assigned` when the scheduled visit is < 24 hours away. Other cancellable
 * statuses (pending, in_review, accepted, rejected) ignore the lead time.
 */
const CANCEL_GATED_24H: readonly ServiceRequestStatus[] = ['assigned'];

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

export type CancellationBlockReason = 'WITHIN_24H';

export interface CancellationEligibility {
  canCancel: boolean;
  reason?: CancellationBlockReason;
}

/**
 * Soft FE gate for showing the "Cancelled" option in the status-update dropdown.
 *
 * - For non-gated statuses, cancellation is always allowed.
 * - For gated statuses (currently just `assigned`), cancellation is blocked
 *   only when `scheduledStartAt` is non-null AND the visit is < 24h away.
 *
 * The BE remains authoritative; this gate is only for hiding the option in the
 * dropdown and showing the explanatory banner on the SR view page.
 */
export const isCancellableNow = (
  status: ServiceRequestStatus | undefined,
  scheduledStartAt: string | null | undefined,
  now: number = Date.now()
): CancellationEligibility => {
  if (!status || !CANCEL_GATED_24H.includes(status)) {
    return { canCancel: true };
  }
  if (!scheduledStartAt) {
    return { canCancel: true };
  }
  const startMs = Date.parse(scheduledStartAt);
  if (Number.isNaN(startMs)) {
    return { canCancel: true };
  }
  if (startMs - now < TWENTY_FOUR_HOURS_MS) {
    return { canCancel: false, reason: 'WITHIN_24H' };
  }
  return { canCancel: true };
};
